import React, { useState } from 'react';
import './CarbonCalculator.css';
import { PUNE_CONSTANTS } from '../data/speciesFormulas';

type CalculatorType = 'transport' | 'clothing' | 'food' | 'electronics' | 'streaming' | 'beverages';
type TransportMode = 'car' | 'motorbike' | 'autoRickshaw' | 'flight';
type ClothingType = 'tshirtCotton' | 'tshirtSynthetic' | 'tshirtOrganic';
type FoodType = 'rice' | 'chicken' | 'vegetables';
type ElectronicsType = 'smartphone' | 'laptop';
type StreamingQuality = 'sd' | 'hd' | 'fourK';
type BeverageType = 'coffeeBlack' | 'coffeeLatte' | 'teaBlack' | 'teaMilk';

const CarbonCalculator: React.FC = () => {
  const [calculatorType, setCalculatorType] = useState<CalculatorType>('transport');
  const [emission, setEmission] = useState<number | null>(null);

  // Transport calculator state
  const [transportMode, setTransportMode] = useState<TransportMode>('car');
  const [distance, setDistance] = useState<string>('');

  // Clothing calculator state
  const [clothingType, setClothingType] = useState<ClothingType>('tshirtCotton');
  const [clothingQuantity, setClothingQuantity] = useState<string>('');

  // Food calculator state
  const [foodType, setFoodType] = useState<FoodType>('rice');
  const [foodWeight, setFoodWeight] = useState<string>('');

  // Electronics calculator state
  const [electronicsType, setElectronicsType] = useState<ElectronicsType>('smartphone');
  const [electronicsQuantity, setElectronicsQuantity] = useState<string>('');

  // Streaming calculator state
  const [streamingQuality, setStreamingQuality] = useState<StreamingQuality>('hd');
  const [streamingHours, setStreamingHours] = useState<string>('');

  // Beverages calculator state
  const [beverageType, setBeverageType] = useState<BeverageType>('coffeeBlack');
  const [beverageQuantity, setBeverageQuantity] = useState<string>('');

  const emissionFactors = PUNE_CONSTANTS.emissionFactors;
  const everydayEmissions = PUNE_CONSTANTS.everydayEmissions;
  const treeOffset = 19; // kg CO₂ absorbed per tree per year

  const calculatorTabs = [
    { id: 'transport' as CalculatorType, icon: '🚗', label: 'Transport' },
    { id: 'clothing' as CalculatorType, icon: '👕', label: 'Clothing' },
    { id: 'food' as CalculatorType, icon: '🍽️', label: 'Food' },
    { id: 'electronics' as CalculatorType, icon: '📱', label: 'Electronics' },
    { id: 'streaming' as CalculatorType, icon: '📺', label: 'Streaming' },
    { id: 'beverages' as CalculatorType, icon: '☕', label: 'Beverages' },
  ];

  const resetCalculator = () => {
    setEmission(null);
    setDistance('');
    setClothingQuantity('');
    setFoodWeight('');
    setElectronicsQuantity('');
    setStreamingHours('');
    setBeverageQuantity('');
  };

  const handleTabChange = (type: CalculatorType) => {
    setCalculatorType(type);
    resetCalculator();
  };

  // Transport Calculator
  const calculateTransportEmission = () => {
    const distanceNum = parseFloat(distance);
    if (isNaN(distanceNum) || distanceNum <= 0) {
      alert('Please enter a valid distance');
      return;
    }
    const co2Emission = distanceNum * emissionFactors[transportMode];
    setEmission(co2Emission);
  };

  const transportModes = {
    car: { icon: '🚗', label: 'Car (Petrol/Diesel)', factor: emissionFactors.car, description: 'Average Indian passenger car' },
    motorbike: { icon: '🏍️', label: 'Motorbike/Scooter', factor: emissionFactors.motorbike, description: '100-150cc two-wheeler' },
    autoRickshaw: { icon: '🛺', label: 'Auto-rickshaw (CNG)', factor: emissionFactors.autoRickshaw, description: 'CNG three-wheeler' },
    flight: { icon: '✈️', label: 'Flight (Domestic)', factor: emissionFactors.flight, description: 'Domestic flight per passenger' },
  };

  // Clothing Calculator
  const calculateClothingEmission = () => {
    const quantity = parseFloat(clothingQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }
    const co2Emission = quantity * everydayEmissions.clothing[clothingType];
    setEmission(co2Emission);
  };

  const clothingTypes = {
    tshirtCotton: { icon: '👕', label: 'Cotton T-shirt', factor: everydayEmissions.clothing.tshirtCotton, description: 'Standard cotton t-shirt' },
    tshirtSynthetic: { icon: '👔', label: 'Synthetic T-shirt', factor: everydayEmissions.clothing.tshirtSynthetic, description: 'Polyester/blend t-shirt' },
    tshirtOrganic: { icon: '🌿', label: 'Organic Cotton T-shirt', factor: everydayEmissions.clothing.tshirtOrganic, description: 'Organic cotton t-shirt' },
  };

  // Food Calculator
  const calculateFoodEmission = () => {
    const weight = parseFloat(foodWeight);
    if (isNaN(weight) || weight <= 0) {
      alert('Please enter a valid weight');
      return;
    }
    const co2Emission = weight * everydayEmissions.food[foodType];
    setEmission(co2Emission);
  };

  const foodTypes = {
    rice: { icon: '🍚', label: 'Rice', factor: everydayEmissions.food.rice, description: '3.7 kg CO₂/kg - Paddy field methane' },
    chicken: { icon: '🍗', label: 'Chicken', factor: everydayEmissions.food.chicken, description: '6.1 kg CO₂/kg - Moderate emissions' },
    vegetables: { icon: '🥬', label: 'Vegetables', factor: everydayEmissions.food.vegetables, description: '0.5 kg CO₂/kg - Lowest footprint' },
  };

  // Electronics Calculator
  const calculateElectronicsEmission = () => {
    const quantity = parseFloat(electronicsQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }
    const co2Emission = quantity * everydayEmissions.electronics[electronicsType];
    setEmission(co2Emission);
  };

  const electronicsTypes = {
    smartphone: { icon: '📱', label: 'Smartphone', factor: everydayEmissions.electronics.smartphone, description: '75 kg CO₂ per device (manufacturing)' },
    laptop: { icon: '💻', label: 'Laptop', factor: everydayEmissions.electronics.laptop, description: '250 kg CO₂ per device (manufacturing)' },
  };

  // Streaming Calculator
  const calculateStreamingEmission = () => {
    const hours = parseFloat(streamingHours);
    if (isNaN(hours) || hours <= 0) {
      alert('Please enter a valid number of hours');
      return;
    }
    const co2Emission = hours * everydayEmissions.streaming[streamingQuality];
    setEmission(co2Emission);
  };

  const streamingQualities = {
    sd: { icon: '📺', label: 'SD Quality (480p)', factor: everydayEmissions.streaming.sd, description: '30g CO₂/hour' },
    hd: { icon: '📺', label: 'HD Quality (1080p)', factor: everydayEmissions.streaming.hd, description: '45g CO₂/hour' },
    fourK: { icon: '📺', label: '4K Quality (2160p)', factor: everydayEmissions.streaming.fourK, description: '90g CO₂/hour' },
  };

  // Beverages Calculator
  const calculateBeverageEmission = () => {
    const quantity = parseFloat(beverageQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }
    const co2Emission = quantity * everydayEmissions.beverages[beverageType];
    setEmission(co2Emission);
  };

  const beverageTypes = {
    coffeeBlack: { icon: '☕', label: 'Black Coffee', factor: everydayEmissions.beverages.coffeeBlack, description: '21g CO₂ per cup' },
    coffeeLatte: { icon: '☕', label: 'Coffee Latte (with milk)', factor: everydayEmissions.beverages.coffeeLatte, description: '340g CO₂ per cup' },
    teaBlack: { icon: '🍵', label: 'Black Tea', factor: everydayEmissions.beverages.teaBlack, description: '20g CO₂ per cup' },
    teaMilk: { icon: '🍵', label: 'Tea with Milk (Chai)', factor: everydayEmissions.beverages.teaMilk, description: '50g CO₂ per cup' },
  };

  const getTreeOffset = (emissionKg: number) => {
    return (emissionKg / treeOffset).toFixed(2);
  };

  return (
    <div className="carbon-calculator-page">
      <div className="carbon-calculator">
        <div className="calculator-header">
          <h2>🌱 Carbon Footprint Calculator</h2>
          <p>Calculate emissions from everyday activities with India-specific data</p>
        </div>

        {/* Calculator Type Tabs */}
        <div className="calculator-tabs">
          {calculatorTabs.map(tab => (
            <button
              key={tab.id}
              className={`calculator-tab ${calculatorType === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Transport Calculator */}
        {calculatorType === 'transport' && (
          <div className="calculator-form">
            <div className="form-group">
              <label htmlFor="transport-mode">Select Transport Mode:</label>
              <select
                id="transport-mode"
                value={transportMode}
                onChange={(e) => {
                  setTransportMode(e.target.value as TransportMode);
                  setEmission(null);
                }}
                className="mode-select"
              >
                {Object.entries(transportModes).map(([key, details]) => (
                  <option key={key} value={key}>
                    {details.icon} {details.label}
                  </option>
                ))}
              </select>
              <small className="mode-description">{transportModes[transportMode].description}</small>
              <div className="emission-factor-info">
                <span>Emission Factor: <strong>{(transportModes[transportMode].factor * 1000).toFixed(0)}g CO₂/km</strong></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="distance">Distance Travelled (km):</label>
              <input
                id="distance"
                type="number"
                value={distance}
                onChange={(e) => {
                  setDistance(e.target.value);
                  setEmission(null);
                }}
                placeholder="Enter distance in kilometers"
                className="distance-input"
                min="0"
                step="0.1"
              />
            </div>

            <div className="calculator-buttons">
              <button onClick={calculateTransportEmission} className="calculate-btn">
                Calculate Emission
              </button>
              <button onClick={resetCalculator} className="reset-btn">
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Clothing Calculator */}
        {calculatorType === 'clothing' && (
          <div className="calculator-form">
            <div className="form-group">
              <label htmlFor="clothing-type">Select Clothing Type:</label>
              <select
                id="clothing-type"
                value={clothingType}
                onChange={(e) => {
                  setClothingType(e.target.value as ClothingType);
                  setEmission(null);
                }}
                className="mode-select"
              >
                {Object.entries(clothingTypes).map(([key, details]) => (
                  <option key={key} value={key}>
                    {details.icon} {details.label}
                  </option>
                ))}
              </select>
              <small className="mode-description">{clothingTypes[clothingType].description}</small>
              <div className="emission-factor-info">
                <span>Emission Factor: <strong>{clothingTypes[clothingType].factor} kg CO₂ per item</strong></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="clothing-quantity">Number of Items:</label>
              <input
                id="clothing-quantity"
                type="number"
                value={clothingQuantity}
                onChange={(e) => {
                  setClothingQuantity(e.target.value);
                  setEmission(null);
                }}
                placeholder="Enter number of t-shirts"
                className="distance-input"
                min="0"
                step="1"
              />
            </div>

            <div className="calculator-buttons">
              <button onClick={calculateClothingEmission} className="calculate-btn">
                Calculate Emission
              </button>
              <button onClick={resetCalculator} className="reset-btn">
                Reset
              </button>
            </div>

            <div className="india-note">
              <strong>India Context:</strong> Indian textile industry is a major global producer. Choosing organic cotton or extending garment lifespan significantly reduces carbon footprint.
            </div>
          </div>
        )}

        {/* Food Calculator */}
        {calculatorType === 'food' && (
          <div className="calculator-form">
            <div className="form-group">
              <label htmlFor="food-type">Select Food Type:</label>
              <select
                id="food-type"
                value={foodType}
                onChange={(e) => {
                  setFoodType(e.target.value as FoodType);
                  setEmission(null);
                }}
                className="mode-select"
              >
                {Object.entries(foodTypes).map(([key, details]) => (
                  <option key={key} value={key}>
                    {details.icon} {details.label}
                  </option>
                ))}
              </select>
              <small className="mode-description">{foodTypes[foodType].description}</small>
              <div className="emission-factor-info">
                <span>Emission Factor: <strong>{foodTypes[foodType].factor} kg CO₂/kg</strong></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="food-weight">Weight (kg):</label>
              <input
                id="food-weight"
                type="number"
                value={foodWeight}
                onChange={(e) => {
                  setFoodWeight(e.target.value);
                  setEmission(null);
                }}
                placeholder="Enter weight in kilograms"
                className="distance-input"
                min="0"
                step="0.1"
              />
            </div>

            <div className="calculator-buttons">
              <button onClick={calculateFoodEmission} className="calculate-btn">
                Calculate Emission
              </button>
              <button onClick={resetCalculator} className="reset-btn">
                Reset
              </button>
            </div>

            <div className="india-note">
              <strong>India Context:</strong> Rice is a staple crop with significant methane emissions from paddy fields. Plant-based diets and locally-grown vegetables have much lower carbon footprints.
            </div>
          </div>
        )}

        {/* Electronics Calculator */}
        {calculatorType === 'electronics' && (
          <div className="calculator-form">
            <div className="form-group">
              <label htmlFor="electronics-type">Select Device Type:</label>
              <select
                id="electronics-type"
                value={electronicsType}
                onChange={(e) => {
                  setElectronicsType(e.target.value as ElectronicsType);
                  setEmission(null);
                }}
                className="mode-select"
              >
                {Object.entries(electronicsTypes).map(([key, details]) => (
                  <option key={key} value={key}>
                    {details.icon} {details.label}
                  </option>
                ))}
              </select>
              <small className="mode-description">{electronicsTypes[electronicsType].description}</small>
              <div className="emission-factor-info">
                <span>Emission Factor: <strong>{electronicsTypes[electronicsType].factor} kg CO₂ per device</strong></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="electronics-quantity">Number of Devices:</label>
              <input
                id="electronics-quantity"
                type="number"
                value={electronicsQuantity}
                onChange={(e) => {
                  setElectronicsQuantity(e.target.value);
                  setEmission(null);
                }}
                placeholder="Enter number of devices"
                className="distance-input"
                min="0"
                step="1"
              />
            </div>

            <div className="calculator-buttons">
              <button onClick={calculateElectronicsEmission} className="calculate-btn">
                Calculate Emission
              </button>
              <button onClick={resetCalculator} className="reset-btn">
                Reset
              </button>
            </div>

            <div className="india-note">
              <strong>India Context:</strong> 85-95% of device emissions come from manufacturing. Extending device lifespan from 2 to 3 years can save ~25 kg CO₂. E-waste recycling is crucial.
            </div>
          </div>
        )}

        {/* Streaming Calculator */}
        {calculatorType === 'streaming' && (
          <div className="calculator-form">
            <div className="form-group">
              <label htmlFor="streaming-quality">Select Video Quality:</label>
              <select
                id="streaming-quality"
                value={streamingQuality}
                onChange={(e) => {
                  setStreamingQuality(e.target.value as StreamingQuality);
                  setEmission(null);
                }}
                className="mode-select"
              >
                {Object.entries(streamingQualities).map(([key, details]) => (
                  <option key={key} value={key}>
                    {details.icon} {details.label}
                  </option>
                ))}
              </select>
              <small className="mode-description">{streamingQualities[streamingQuality].description}</small>
              <div className="emission-factor-info">
                <span>Emission Factor: <strong>{(streamingQualities[streamingQuality].factor * 1000).toFixed(0)}g CO₂/hour</strong></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="streaming-hours">Streaming Hours:</label>
              <input
                id="streaming-hours"
                type="number"
                value={streamingHours}
                onChange={(e) => {
                  setStreamingHours(e.target.value);
                  setEmission(null);
                }}
                placeholder="Enter number of hours"
                className="distance-input"
                min="0"
                step="0.5"
              />
            </div>

            <div className="calculator-buttons">
              <button onClick={calculateStreamingEmission} className="calculate-btn">
                Calculate Emission
              </button>
              <button onClick={resetCalculator} className="reset-btn">
                Reset
              </button>
            </div>

            <div className="india-note">
              <strong>India Context:</strong> India's grid carbon intensity (0.716 kg CO₂/kWh) is higher than global average. Watching in SD instead of 4K reduces emissions by 67%.
            </div>
          </div>
        )}

        {/* Beverages Calculator */}
        {calculatorType === 'beverages' && (
          <div className="calculator-form">
            <div className="form-group">
              <label htmlFor="beverage-type">Select Beverage Type:</label>
              <select
                id="beverage-type"
                value={beverageType}
                onChange={(e) => {
                  setBeverageType(e.target.value as BeverageType);
                  setEmission(null);
                }}
                className="mode-select"
              >
                {Object.entries(beverageTypes).map(([key, details]) => (
                  <option key={key} value={key}>
                    {details.icon} {details.label}
                  </option>
                ))}
              </select>
              <small className="mode-description">{beverageTypes[beverageType].description}</small>
              <div className="emission-factor-info">
                <span>Emission Factor: <strong>{(beverageTypes[beverageType].factor * 1000).toFixed(0)}g CO₂ per cup</strong></span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="beverage-quantity">Number of Cups (Annual):</label>
              <input
                id="beverage-quantity"
                type="number"
                value={beverageQuantity}
                onChange={(e) => {
                  setBeverageQuantity(e.target.value);
                  setEmission(null);
                }}
                placeholder="e.g., 730 cups = 2 cups/day for a year"
                className="distance-input"
                min="0"
                step="1"
              />
            </div>

            <div className="calculator-buttons">
              <button onClick={calculateBeverageEmission} className="calculate-btn">
                Calculate Emission
              </button>
              <button onClick={resetCalculator} className="reset-btn">
                Reset
              </button>
            </div>

            <div className="india-note">
              <strong>India Context:</strong> Tea is a major crop in India. Black tea has 86% lower footprint than coffee latte (20g vs 340g). Milk adds significantly to emissions.
            </div>
          </div>
        )}

        {/* Results Section */}
        {emission !== null && (
          <div className="results-section">
            <div className="emission-result">
              <h3>Your Carbon Footprint</h3>
              <div className="result-card">
                <div className="emission-value">
                  <span className="value">{emission.toFixed(2)}</span>
                  <span className="unit">kg CO₂</span>
                </div>
                <div className="tree-offset">
                  <span className="tree-icon">🌳</span>
                  <span className="tree-text">
                    Equivalent to <strong>{getTreeOffset(emission)}</strong> tree-years of carbon absorption
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarbonCalculator;
